/**
 * The player-facing changelog.
 *
 * Every entry is written for someone who plays the game, not someone who reads
 * the code: say what changed in the app and what it means at the table. No
 * file names, no function names, no commit hashes.
 *
 * ADD AN ENTRY WHENEVER A CHANGE AFFECTS WHAT PLAYERS SEE OR CAN DO. Newest
 * first. The homepage shows the first few; /updates shows all of them.
 *
 * Purely internal work (tests, refactors, tooling) does not belong here - if a
 * player cannot notice it, it is not an update. The exception is accuracy work
 * that changes what the army checker accepts or rejects, because that does
 * change what players can build.
 */

export type UpdateTag = 'Army lists' | 'Game modes' | 'Rules accuracy' | 'App';

export interface Update {
  id: string;
  /** Display date, e.g. "7 Aug 2026" */
  date: string;
  /** Sortable date, YYYY-MM-DD */
  isoDate: string;
  tag: UpdateTag;
  title: string;
  /** One-paragraph summary, shown on the homepage and the updates page */
  body: string;
  /** Extra detail shown only on the updates page */
  detail?: string[];
  /** Optional call to action */
  to?: string;
  cta?: string;
  accent: string;
}

export const UPDATES: Update[] = [
  {
    id: 'share-cancel-fix',
    date: '9 Aug 2026',
    isoDate: '2026-08-09',
    tag: 'App',
    title: 'Sharing a list on mobile no longer errors when cancelled',
    body:
      'Closing your phone\'s share sheet without picking an app used to trigger an error and an ' +
      'unwanted copy to the clipboard. Cancelling now just cancels, and when copying is the intended ' +
      'fallback it works reliably even right after the share sheet closes.',
    accent: 'var(--f-primary)',
  },
  {
    id: 'master-of-the-black-arts',
    date: '9 Aug 2026',
    isoDate: '2026-08-09',
    tag: 'Rules accuracy',
    title: 'Master of the Black Arts raises the wizard level',
    body:
      'A sweep for more upgrades like Gnarled Hide found one: the Vampire Counts\' Master of the ' +
      'Black Arts ("increases their Level of Wizardry by 1") now counts wherever wizard level ' +
      'matters - the spell slots in the game tracker\'s wizard setup, and eligibility for powers ' +
      'restricted by wizard level. It applies in all three Vampire Counts lists.',
    accent: 'var(--f-cat-characters)',
  },
  {
    id: 'granted-rules-in-saves',
    date: '9 Aug 2026',
    isoDate: '2026-08-09',
    tag: 'Rules accuracy',
    title: 'Mutations and upgrades now count in the save',
    body:
      'The armour save now reads the rules your selections grant, not just the ones printed on the ' +
      'unit. A Bray-Shaman with the Gnarled Hide mutation shows 6+ (Armoured Hide from no armour), ' +
      'and 3+ once you add Full Plate Chaos Armour. The same applies to any option, honour or magic ' +
      'item that grants Armoured Hide. Thanks again to the Discord crew for the sharp eyes.',
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'armoured-hide-and-caps',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Armoured Hide, lone shields and armour caps',
    body:
      'Three more armour save corrections. Armoured Hide now counts: a model with no armour has an ' +
      'effective 7+ that improvement rules work from, so Warped Gors show 6+ and Dragon Ogres 4+. ' +
      'The same rule means a lone shield now shows 6+. And the armour caps from the January FAQ are ' +
      'applied: infantry and cavalry can never be better than 2+, while chariots, monsters and war ' +
      'machines stop at 3+ - monstrous infantry and cavalry count as infantry and cavalry, so they ' +
      'keep the 2+ cap.',
    detail: [
      'A Mage with the Blood of Caledor Honour may now buy magic armour, including the Armour of Caledor: the Honour\'s free full plate opens the armoury as the rulebook requires ("equipped with, or has the option to be equipped with, armour").',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'magic-armour-access',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Wizards can no longer pick armour they cannot wear',
    body:
      'The rulebook only lets a model buy magic armour if it has (or could take) armour, a shield or ' +
      'barding - which is what keeps most Wizards out of it. The builder now enforces this: a ' +
      'Bray-Shaman or Damsel no longer sees ordinary magic suits and shields at all, while items that ' +
      'explicitly waive the rule stay available - Heavy and Full Plate Chaos Armour, the Armour of ' +
      'Tarnus, Warpstone Armour, the Flayed Hauberk and the Warding Splint.',
    detail: [
      'Magic suits need armour access, magic shields need a shield, and worn-with-other-armour pieces (helms, pelts) need any of the three.',
      'The checker also flags these on imported lists, which never go through the picker.',
      'While verifying the waivers against the books, the Armour of Caledor turned out to be missing its "suit of full plate armour" line in our data (FoF p.183) - restored, so it now grants its 4+ save. It has no Wizard waiver in the book, so Mages cannot take it.',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'magic-armour-saves',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Magic armour now counts in the displayed save',
    body:
      'Following the same player reports: a character\'s armour save now includes their magic armour. ' +
      'A Bray-Shaman in Full Plate Chaos Armour shows 4+ instead of no save, an Enchanted Shield adds ' +
      'its point of armour (without stacking with a mundane shield - one shield per model), helms and ' +
      'pelts worn with other armour add their bonus with the printed 2+ cap, and unmodifiable suits ' +
      'like the Armour of Meteoric Iron stay at their fixed value.',
    detail: [
      'Every magic armour item in every faction now carries structured save data - 137 items were classified from their rules text, and two whose text had lost its armour clause (Armour of Tarnus, Twice-Blessed Armour) were restored from the books.',
      'A rule granted twice (a unit with Terror taking a mark that also grants Terror) now shows once in the rules list.',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'banner-budget-and-saves',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Banners stop eating the champion\'s item budget, and saves update properly',
    body:
      'Two fixes from a player report on the Hordes of Chaos Chosen. A unit\'s magic standard was being ' +
      'counted against the champion\'s magic item allowance - they are separate budgets (the standard ' +
      'bearer\'s banner allowance and the champion\'s item allowance), and are now tracked separately. ' +
      'And the armour save shown on a unit\'s card now updates for every armour option: taking Shields ' +
      'or swapping to full plate armour moves the save as it should.',
    detail: [
      'The save previously missed options written in the plural ("Shields") or as "May replace X with Y", so a Chosen unit with shields and full plate still showed 5+. It now reads 3+.',
      'The checker itself always scored these correctly - the display and the picker\'s budget maths were wrong, which could grey out items the champion could legally afford.',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'walkthrough-fixes',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Missing mounts, mutations and models restored',
    body:
      'A full sweep of every option on every unit turned up a handful of dead ends, now fixed: ' +
      'the Renegade Legacy Chaos Dwarf commanders can actually take their Taurus mounts, the Dawn of ' +
      'the Storm Dragon\'s hired Empire characters get their warhorses, Pegasus, Demigryph and Griffon, ' +
      'Warriors of Chaos Dragon Ogres can buy their Chaos Mutations, and Knightly Order armies no ' +
      'longer offer the priests their order forbids.',
    detail: [
      'Chaos Dwarfs (Renegade Legacy v1.5, pack p.8): the Infernal Castellan may ride a Bale Taurus or Great Taurus and the Infernal Seneschal a Great Taurus - the option existed but the mount list was empty.',
      'Warriors of Chaos: Dragon Ogres, the Shaggoth and the Heralds of Darkness\' Centigor Chieftain had Chaos Mutation allowances with nothing to buy; the Beastmen mutation list now travels with them, as the Ravening Hordes list intends.',
      'Renegades Chaos Dwarfs: Goblin Mobs\' "Include 0-3 Nasty Skulkers" now adds the actual Skulkers.',
      'Empire Knightly Orders: a White Wolf army no longer offers Priests of Sigmar in the unit browser (and so on for each order) - previously they could be added and then flagged as illegal.',
    ],
    accent: 'var(--f-cat-rare)',
  },
  {
    id: 'faction-lore-swap-spells',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Game modes',
    title: 'Faction lore spells in the wizard setup',
    body:
      'Wizards with a faction lore rule - the Lore of the Lady, Lore of Hashut, Lore of Saphery, ' +
      'Lore of Undeath and the rest - now see those spells in the game tracker\'s spell setup, under ' +
      'their chosen lore, so you can record a swapped-in spell exactly as the rule allows. ' +
      'Previously there was no way to pick them at all.',
    detail: [
      'Applies to every army with the mechanic: Bretonnia, High Elves, Wood Elves, Dark Elves, Beastmen, Warriors and Daemons of Chaos, Chaos Dwarfs, Lizardmen, Ogre Kingdoms, Skaven, Vampire Counts, Orcs & Goblins (Gork and Mork) and Tomb Kings.',
      'The Renegades 2.0 and Renegade Legacy revisions show their own versions of these spells where they differ.',
      'Thanks to Des and Dan for the report.',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'stat-lines-restored',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'App',
    title: 'Stat lines restored on 22 units',
    body:
      'Orc & Goblin chariots, boar boys, wolf and spider riders, squig units, trolls, pump wagons and ' +
      'all four war machines - plus the Arachnarok, Badlands Ogre Bulls, the Beastmen Primal Warherd ' +
      'and both Beastmen chariots, and the Chosen Chaos Chariot - were showing no characteristics at ' +
      'all in the builder. Every unit now shows its full stat line, including mount and crew rows.',
    detail: [
      'Champion stat rows now display when you add the champion on units that carry one (an Orc Boar Boy Boss, an Ogre Crusher), covering about forty units whose champion characteristics were stored but never shown.',
      'Chariots now show their movement in the body row with the beasts on their own line, matching how other armies\' chariots already displayed.',
    ],
    accent: 'var(--f-primary)',
  },
  {
    id: 'renegades-slann-disciplines',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Renegades Slann can take its Disciplines',
    body:
      'The Renegades 2.0 Slann Mage-Priest\'s "Purchase 0-3 Disciplines of the Old Ones" entry now works: ' +
      'a Disciplines picker appears on the Slann with the full list of nine, tracking the 100-point budget ' +
      'and the three-pick limit. Previously the option bought nothing.',
    detail: [
      'Each Discipline can be taken once per army, and only by a Slann Mage-Priest - both now enforced.',
      'The classic Lizardmen and Renegade Legacy lists are unchanged: their Slann still picks a single Discipline.',
    ],
    accent: 'var(--f-cat-characters)',
  },
  {
    id: 'honour-pickers-restored',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Virtues, Honours and Big Names are back in the picker',
    body:
      'Bretonnian Knightly Virtues, Elven Honours and Ogre Big Names had mostly vanished from the ' +
      'character upgrade dropdown - a side effect of the FAQ text added to their restrictions. The ' +
      'picker now checks who may take each upgrade properly, so every legal choice is offered again ' +
      'and character-only ones stay off units that cannot take them.',
    detail: [
      'Bretonnia: all fourteen Virtues show for Dukes, Barons and Paladins, and the Paragon of a Questing or Grail Knights unit gets the ten that are not character-only.',
      'High Elves: all eight Honours show again (only Loremaster is Prince/Noble-only) - which also means taking the Chracian Hunter or Sea Guard Honour once more unlocks the Lion Chariot of Chrace and Lothern Skycutter Special choices.',
      'Ogre Kingdoms (all three lists): Mawseeker, Mountaineater, Kineater and Longstrider are selectable again.',
    ],
    accent: 'var(--f-cat-characters)',
  },
  {
    id: 'unlocked-core-choices',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Units unlocked as Core now appear in Core',
    body:
      'Where a list says something like "if your General is a Handmaiden of the Everqueen, 0-1 unit of ' +
      'Sisters of Avelorn may be taken as a Core choice", that choice is now a real entry in the Core ' +
      'section - and it only appears once you have met the condition. Sixty of these across eighteen ' +
      'armies were previously counted in the right slot but never shown there.',
    detail: [
      'Daemons of Chaos benefits most: with a Khorne General the Core section offers Bloodletters, Flesh Hounds and Chaos Furies, and none of the Nurgle, Slaanesh or Tzeentch entries you cannot take.',
      'The standard entry is untouched, so you can still take more of the unit in its usual slot beyond the one the list unlocks.',
    ],
    accent: 'var(--f-cat-core)',
  },
  {
    id: 'hordes-common-items',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Hordes of Chaos: Common magic items are back',
    body:
      'In the Hordes of Chaos list, models borrowed from the Warriors of Chaos and Beastmen books buy from ' +
      'their own book plus the Common magic items. The Common items were being filtered out entirely, so ' +
      'magic standards and a lot of magic weapons simply never appeared. They are available again, and a ' +
      'model still cannot reach into the other parent book.',
    accent: 'var(--f-cat-rare)',
  },
  {
    id: 'extremely-common-items-and-chosen',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Rules accuracy',
    title: 'Extremely common items stack, and Chosen can hold the line',
    body:
      'Two fixes from player reports. Items marked with an asterisk are extremely common, and the rulebook ' +
      'lets you buy them alongside another item from the same category and take as many as you like across ' +
      'the army - the builder was blocking both. And in the Hordes of Chaos list, a unit of Chosen Chaos ' +
      'Warriors now actually counts as Core when your General is a Chaos Lord, Exalted Champion or Daemon ' +
      'Prince, which is what the list says.',
    detail: [
      'So a character can now carry, say, a Favour of the Gods and a second talisman, and two characters can carry the same extremely common item. Extremely common items also show a + and - counter rather than a tick box, because a character may carry more than one copy.',
      'The Chosen slot was described in the list but never took effect, so their points still counted as Special and could push an army over its Special allowance.',
    ],
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'renegade-legacy-15',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Renegade Legacy Pack v1.5',
    body:
      'The v1.5 Renegade lists are being added as their own set, separate from the 2.0 lists already in ' +
      'the app. v1.5 is the revision most events play; 2.0 is still in progress, and is now labelled as ' +
      'beta when you pick an army. Chaos Dwarfs is in, with the rest following.',
    detail: [
      'Chaos Dwarfs and Daemons of Chaos are in, with the rest following. v1.5 applies its changes on top of the official Legends lists, so each army is the Legends list with the pack\'s amendments - for Chaos Dwarfs that means the Bull Centaur Taur\'ruk moves to Rare, K\'daai Fireborn gain a third Wound and Born of Fire, the Great Taurus becomes a Monstrous Creature on a 60x100 base, and the Infernal Castellan and Seneschal can take Taurus mounts.',
    ],
    accent: 'var(--f-cat-rare)',
  },
  {
    id: 'cross-list-borrows',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Borrowed units arrive across five armies',
    body:
      'Every unit an army list borrows from another book is now actually in the builder. Warriors of ' +
      'Chaos gain their Dragon Ogres, Shaggoth and Chaos Giant from the Beastmen list - and the ' +
      'Wolves of the Sea can hire Gor and Ungor Herds, while the Heralds of Darkness take Centigor ' +
      'mercenaries. Beastmen gain Chaos Ogres, Trolls and Spawn. The Empire can hire Imperial Dwarf ' +
      'Mercenaries and Doomseekers, Bretonnian Exiles and the Errantry Crusade get their Empire ' +
      'mercenaries, and the Dawn of the Storm Dragon hires its full Empire contingent.',
    detail: [
      'The book conditions come with them: 0-1 Shaggoth per unit of Dragon Ogres, 0-1 Chaos Giant per 1,000 points, 0-1 Imperial Dwarf unit and 0-3 Doomseekers in an Empire Grand Army, 0-1 unit of Pistoliers or Outriders per 1,000 in the Dawn of the Storm Dragon.',
      'Borrowed units keep their home-book profiles and points, which are already current to the latest FAQs.',
      'Borrowed models shop from their own book: a Black Orc Boss in a Chaos Dwarf army buys Orc & Goblin magic items plus Commons - never Chaos Dwarf items - and an Empire Captain hired by Grand Cathay buys from the Empire list. The builder offers and enforces exactly that.',
    ],
    to: '/army-builder/new',
    cta: 'Open the army builder',
    accent: 'var(--f-cat-rare)',
  },
  {
    id: 'chaos-dwarf-greenskins',
    date: '8 Aug 2026',
    isoDate: '2026-08-08',
    tag: 'Army lists',
    title: 'Chaos Dwarfs: the greenskins report for duty',
    body:
      'Both Chaos Dwarf lists borrow units from other books that were missing from the builder. ' +
      'The Legends list gains its 0-1 Black Orc Boss and 0-1 Black Orc Mob (with the bosses\' War ' +
      'Boar and Wyvern mounts) and the Hellcannon in the war machine pool. The Renegades list gains ' +
      'all of those plus Chaos Giants, and Orc Mobs, Goblin Mobs and the Bonegrinder Giant as ' +
      'mercenaries. In Renegades, Black Orc Mobs correctly require a Black Orc Boss; in Legends ' +
      'they do not.',
    to: '/army-builder/new',
    cta: 'Build a Chaos Dwarf army',
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'builder-fixes-mounts-rules-items',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'App',
    title: 'Builder fixes: mounts everywhere, rule badges, item lists',
    body:
      'A sweep of every army found the mount picker broken well beyond Warriors of Chaos: over ' +
      'forty character mounts across eleven armies - Black Dragons, Zombie Dragons, Coven Thrones, ' +
      'Screaming Bells, Tauruses, Carnosaurs and more - never appeared as choices. All fixed, with ' +
      'each mount offered only to the riders its book allows. Anything that adds a special rule - ' +
      'unit options like Vanguard on Chaos Warhounds, upgrades like Longstriders or Brazen Will, ' +
      'magic items - now shows that rule as a badge on the unit card, clickable through to the ' +
      'rules reference. And magic item lists no longer show items that cost more than the model\'s ' +
      'whole allowance.',
    accent: 'var(--f-cat-mercenaries)',
  },
  {
    id: 'battle-march-mode',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Game modes',
    title: 'Battle March in the General\'s Adjutant',
    body:
      'Battle March is now a mode you can pick when you set up a game. Roll for the objectives, roll or ' +
      'choose one of the six deployment maps and see its zones drawn out, roll the Unusual Property for a ' +
      'strategic landmark, and roll on whichever Random Happenings table you agreed to use. The Battle ' +
      'March Victory Points and both secondary objectives are there too.',
    detail: [
      'All six deployment maps are drawn with the dimensions printed in the book: Pitched Battle, Close Encounter, Opposed Flanks, Meeting Engagement, Mountain Pass and Outflank. The four the book allows to be reversed have a mirror button.',
      'All three Random Happenings tables are included in full - Disruptive Weather, Wilderness Terrain and the Chaos of War - along with the Enraged Troll and Wandering Mercenaries profiles they can bring onto the table.',
    ],
    to: '/turn-tracker',
    cta: 'Open the Adjutant',
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'item-effects-and-sorting',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'App',
    title: 'Magic items show their effects on the profile',
    body:
      'Items that change the bearer\'s characteristics or grant special rules now show it on the ' +
      'unit card. Take the Mantle of Drifting Shadow and the profile shows the +1 Movement and the ' +
      'Loner badge; the Chaos Runesword adds its +1 WS and +1 Initiative; the Accursed Armour shows ' +
      'both what it gives and what it costs you. Magic item lists are also now sorted most expensive ' +
      'first within each category.',
    detail: [
      'Only effects that are always on appear on the profile. Bonuses that depend on charging, on wounds taken, on auras or on the unit carrying a banner stay in the item text, where the condition is spelled out.',
      'Around sixty items that grant special rules in their text - Terror, Frenzy, Poisoned Attacks, Vanguard, Ethereal and more - now show those as rule badges when carried.',
    ],
    accent: 'var(--f-cat-core)',
  },
  {
    id: 'chaotic-traits',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Army lists',
    title: 'Warriors of Chaos: Chaotic Traits',
    body:
      'The Chaotic Traits from The Razing of Westerland are in the builder. Characters and units in a ' +
      'Warriors of Chaos army - the Grand Army or either Army of Infamy - can now take Unnatural ' +
      'Fortitude, Dark Hearts, Longstriders, Enhanced Reflexes, Battle Hunger, Brazen Will and ' +
      'Prophetic Foresight.',
    detail: [
      'Prices follow the book exactly: characters pay one rate, units another - per model for some traits (Unnatural Fortitude is +2 a model), per unit for others (Battle Hunger is +20 for the whole regiment).',
      'Allowances match the book: Chaos Lords, Sorcerer Lords, Chosen and Marauder Huscarls may take two; everyone else listed may take one.',
      'The restrictions are enforced - Battle Hunger is infantry only, Dark Hearts and Brazen Will need infantry or cavalry, and only one unit per 1,000 points may take Longstriders.',
    ],
    to: '/army-builder/new',
    cta: 'Build a Warriors of Chaos army',
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'options-blank-page-fix',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'App',
    title: 'Fixed: blank page when opening a character\'s options',
    body:
      'Expanding the Options panel on a Wizard - or any character who could become one - briefly ' +
      'showed a blank page. Fixed the same day; if you hit it, it works now.',
    accent: 'var(--f-cat-mercenaries)',
  },
  {
    id: 'march-of-chaos',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Army lists',
    title: 'March of Chaos — Hordes of Chaos',
    body:
      'The new Arcane Journal army is in the builder. Hordes of Chaos lets you field Warriors of Chaos ' +
      'and Beastmen side by side in one force, with two new units — Chaos Cultists and the Cult Magister ' +
      'who leads them. You will find it under Arcane Journals when you start a new army.',
    detail: [
      'Your core must include both halves of the alliance: at least one unit of Chaos Marauders or Chaos Warriors, and at least one Gor or Ungor Herd.',
      'Who you pick as General changes what you can take. A Chaos Lord, Exalted Champion or Daemon Prince unlocks Chosen Chaos Warriors and Chosen Chaos Knights; a Beastlord or Wargor unlocks Bestigor Herds instead.',
      'Magic items follow the model. A Warriors of Chaos character can buy from the Warriors of Chaos list, a Beastmen character from the Beastmen list, and everyone can buy Common items. The builder only offers each model what it may actually take.',
      'The book is not out yet. This was built from photographs of a pre-release copy, so treat points and rules as provisional — we will re-check every page against the printed book on release.',
    ],
    to: '/army-builder/new',
    cta: 'Muster a Hordes of Chaos army',
    accent: 'var(--f-cat-special)',
  },
  {
    id: 'renegades',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Army lists',
    title: 'Renegades army lists',
    body:
      'All seven community Renegades rewrites are now in the army builder: Vampire Counts, Dark Elves, ' +
      'Chaos Dwarfs, Lizardmen, Ogre Kingdoms, Skaven and Daemons of Chaos. They sit under ' +
      'Square Based / Renegades Legends when you start a new army, and the official Legends lists are ' +
      'untouched — pick whichever your group plays.',
    detail: [
      'Each Renegades list is complete and separate: its own units, points, magic items, spells and composition rules.',
      'Where a Renegades list changes a rule, the builder follows that list rather than the official one. A Dark Elf army can field two units of Black Guard in Renegades, for example, where the Legends list allows one per Noble.',
      'Both versions of every army remain available, so nothing you have already built has changed.',
    ],
    to: '/army-builder/new',
    cta: 'Start a Renegades army',
    accent: 'var(--f-cat-rare)',
  },
  {
    id: 'battle-march',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Game modes',
    title: 'Battle March',
    body:
      'The small-scale format is now a matched play option. Pick it when starting an army and the points ' +
      'presets drop to 400–750, the army checks switch to Battle March limits — two units minimum, ' +
      'Unit Strength 20, and per-unit points caps — and the twenty Battle March magic items unlock, ' +
      'hidden from every other list.',
    detail: [
      'Battle March relaxes some rules as well as adding them: a "0-1 per 1,000 points" choice would normally allow nothing at all below 1,000 points, so a Battle March list may include one.',
      'The Battle March magic items are available to every army except Dwarfs, who use their own runic items, and only ever appear when Battle March is selected.',
    ],
    to: '/army-builder/new',
    cta: 'Muster a Battle March force',
    accent: 'var(--f-cat-characters)',
  },
  {
    id: 'army-checker-overhaul',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Rules accuracy',
    title: 'A much stricter army checker',
    body:
      'Every army list in the app has been checked against its book, page by page, and the builder now ' +
      'catches a great deal it used to let through — and, just as importantly, stops complaining about ' +
      'lists that were always legal.',
    detail: [
      'Some armies had no limit on their commanders at all. A Warriors of Chaos or Orc & Goblin list could field three warbosses without a word of complaint; now it cannot.',
      'Two Empire rules were the wrong way round: "0-1 unit of Veteran State Troops per 1,000 points" was being enforced as a requirement, so perfectly legal Empire armies were being marked as broken.',
      'Battle Standard Bearers were missing entirely from eleven armies — including Bretonnia, High Elves, Wood Elves, Dwarfs, Beastmen, Orcs & Goblins and four Legends lists — so you simply could not take one.',
      'Requirements that were written down but never enforced now are: 1+ King or Thane in a Dwarf Royal Clan, 1+ Paladin in an Errantry Crusade, 1+ Gor Herd in a Beastmen army, and many more.',
      'Units a list names but the app had forgotten now appear. Orion\'s Wild Hunt was missing five of its own choices, including Wardancers and Glade Riders.',
      'Magic item budgets, magic standards and once-per-army items are now checked on every model, not just while you are clicking.',
    ],
    to: '/army-builder',
    cta: 'Check an army',
    accent: 'var(--f-primary)',
  },
  {
    id: 'upgrade-picker',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'App',
    title: 'Gifts, Mutations, Powers and Spites are selectable',
    body:
      'Nearly a hundred upgrades existed in the data but could never be bought. Gifts of Chaos, Chaos ' +
      'Mutations, Forest Spites, Vampiric Powers, Daemonic Gifts and the Tomb Kings\' Incantation Scrolls ' +
      'now have a proper picker, with the running total against your allowance.',
    detail: [
      'The picker enforces who may take what: Rune of the Beast Ascendant is Beastlord and Wargor only, Axe of Khorne needs a Daemon of Khorne, Flying Horror is infantry only, and Master of the Black Arts needs the right level of Wizard.',
      'Restrictions that depend on choices you have not made yet resolve as you build. Pick a Daemonic Alignment for your Daemon Prince and that god\'s gifts unlock immediately.',
      'Upgrades that may only be chosen once per army are tracked across the whole list, and Many Arms — the one upgrade a model may take twice — has a 0-2 stepper.',
    ],
    to: '/army-builder',
    cta: 'Open the army builder',
    accent: 'var(--f-cat-core)',
  },
  {
    id: 'incantation-scrolls',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Army lists',
    title: 'Tomb Kings: Incantation Scrolls',
    body:
      'The six Incantation Scrolls from The War of Settra\'s Fury now work the way the book intends — ' +
      'bought from their own allowance rather than out of a character\'s magic items budget.',
    detail: [
      'High Priests may take up to three, Mortuary Priests two, and Necrotects and Arch Necrotects one.',
      'A model cannot take the same scroll twice, and no two characters in your army may carry the same combination — both are checked for you.',
      'They are available to those characters in any Tomb Kings list, not just the Mortuary Cult.',
    ],
    to: '/army-builder/new',
    cta: 'Build a Tomb Kings army',
    accent: 'var(--f-gold)',
  },
  {
    id: 'sorcerous-ruses',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Rules accuracy',
    title: 'Sorcerous Ruses for every Wizard',
    body:
      'Any Wizard in any army may now buy a Sorcerous Ruse for 25 points. The option appears on every ' +
      'model that is a Wizard — including those that only become one by buying a wizard upgrade, or by ' +
      'wearing the Wizarding Hat.',
    accent: 'var(--f-cat-mercenaries)',
  },
  {
    id: 'legends',
    date: '6 Aug 2026',
    isoDate: '2026-08-06',
    tag: 'Army lists',
    title: 'The full Legends range',
    body:
      'Every Legends army list is available: Chaos Dwarfs, Daemons of Chaos, Dark Elves, Lizardmen, ' +
      'Ogre Kingdoms, Skaven and Vampire Counts, each with its own units, magic items and spells.',
    to: '/army-builder/new',
    cta: 'Browse the army lists',
    accent: 'var(--f-cat-core)',
  },
  {
    id: 'faq-current',
    date: '7 Aug 2026',
    isoDate: '2026-08-07',
    tag: 'Rules accuracy',
    title: 'Up to date with the latest FAQs',
    body:
      'All army data matches the current Games Workshop FAQ and errata documents — the Rulebook, ' +
      'Forces of Fantasy, Ravening Hordes, Armies of Grand Cathay and The War of Settra\'s Fury. ' +
      'Points, characteristics, spell casting values and magic item costs all reflect the latest ' +
      'corrections.',
    accent: 'var(--f-cat-special)',
  },
];

/** The most recent entries, for the homepage box. */
export function recentUpdates(count = 3): Update[] {
  return UPDATES.slice(0, count);
}
